import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
export function QrScanner({ onScan, isActive, lastScanResult = null }) {
    const [manualCode, setManualCode] = useState('');
    const [cameraError, setCameraError] = useState(null);
    const [isScanning, setIsScanning] = useState(false);
    const [cameraPermission, setCameraPermission] = useState('prompt');
    const [flashState, setFlashState] = useState(null);
    const html5QrRef = useRef(null);
    const containerRef = useRef(null);
    const lastScannedRef = useRef('');
    const scanCooldownRef = useRef(null);
    // Keep latest onScan in a ref so callbacks don't need it in deps
    const onScanRef = useRef(onScan);
    onScanRef.current = onScan;
    // Flash effect when scan result changes
    useEffect(() => {
        if (lastScanResult) {
            setFlashState(lastScanResult);
            const t = setTimeout(() => setFlashState(null), 800);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [lastScanResult]);
    const stopScanner = useCallback(() => {
        const scanner = html5QrRef.current;
        if (!scanner)
            return;
        scanner
            .stop()
            .then(() => {
            html5QrRef.current = null;
            setIsScanning(false);
        })
            .catch(() => {
            // Scanner may not be fully started yet — ignore "not running" errors.
            html5QrRef.current = null;
            setIsScanning(false);
        });
    }, []);
    const startScanner = useCallback(async () => {
        if (!containerRef.current)
            return;
        // Prevent double-start: if a scanner instance already exists, skip.
        if (html5QrRef.current)
            return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
            stream.getTracks().forEach((t) => t.stop());
            setCameraPermission('granted');
        }
        catch {
            setCameraPermission('denied');
            setCameraError('No se pudo acceder a la cámara. Usa el ingreso manual.');
            return;
        }
        try {
            const html5Qr = new Html5Qrcode(containerRef.current.id);
            html5QrRef.current = html5Qr;
            await html5Qr.start({ facingMode: 'environment' }, {
                fps: 5,
                qrbox: { width: 250, height: 250 },
                aspectRatio: 1,
            }, (decodedText) => {
                // Debounce: ignore same QR within 2s
                if (decodedText === lastScannedRef.current)
                    return;
                lastScannedRef.current = decodedText;
                if (scanCooldownRef.current)
                    clearTimeout(scanCooldownRef.current);
                scanCooldownRef.current = setTimeout(() => {
                    lastScannedRef.current = '';
                }, 2000);
                onScanRef.current(decodedText);
            }, () => {
                // ignore QR detection failures (normal during scanning)
            });
            setIsScanning(true);
            setCameraError(null);
        }
        catch {
            setCameraError('No se pudo iniciar la cámara. Verifica los permisos.');
            setIsScanning(false);
        }
    }, []);
    // Suppress html5-qrcode internal setTimeout violation warnings.
    // These are inherent to the library's frame-processing loop and cannot be avoided.
    useEffect(() => {
        const orig = console.error;
        console.error = (...args) => {
            const msg = args[0];
            if (typeof msg === 'string' && msg.includes("'setTimeout' handler took"))
                return;
            orig.apply(console, args);
        };
        return () => { console.error = orig; };
    }, []);
    useEffect(() => {
        if (isActive) {
            startScanner();
        }
        else {
            stopScanner();
        }
        return () => {
            if (html5QrRef.current) {
                html5QrRef.current.stop().catch(() => { });
                html5QrRef.current = null;
            }
            if (scanCooldownRef.current)
                clearTimeout(scanCooldownRef.current);
        };
    }, [isActive]);
    const handleManualSubmit = (e) => {
        e.preventDefault();
        const code = manualCode.trim();
        if (code) {
            onScan(code);
            setManualCode('');
        }
    };
    const flashColors = {
        success: { bg: 'rgba(34,197,94,0.4)', label: '✓ Registrado' },
        error: { bg: 'rgba(239,68,68,0.4)', label: '✗ Error' },
        duplicate: { bg: 'rgba(245,158,11,0.4)', label: '⚠ Ya registrado' },
    };
    return (_jsxs("div", { className: "flex flex-col gap-4", children: [isActive && (_jsxs("div", { className: "relative", children: [_jsx("div", { id: "qr-reader", ref: containerRef, className: "overflow-hidden rounded-lg border border-[var(--color-border)]", style: { minHeight: 200 } }), flashState && (_jsx("div", { className: "absolute inset-0 rounded-lg flex items-center justify-center transition-opacity duration-300 pointer-events-none", style: { backgroundColor: flashColors[flashState].bg }, children: _jsx("span", { className: "text-white text-xl font-bold drop-shadow", children: flashColors[flashState].label }) })), isScanning && (_jsx("div", { className: "absolute inset-0 flex items-center justify-center pointer-events-none", children: _jsx("div", { className: "w-52 h-52 border-2 border-dashed border-white/60 rounded-lg" }) })), cameraError && (_jsx("div", { className: "mt-3 p-3 rounded-md bg-[var(--color-error-light)] border border-[var(--color-error)]", children: _jsxs("p", { className: "text-xs text-[var(--color-error)] flex items-center gap-1.5", children: [_jsx("span", { className: "font-medium", children: "Error de c\u00E1mara:" }), " ", cameraError] }) })), isScanning && (_jsx("div", { className: "mt-2 text-center", children: _jsx("button", { type: "button", className: "btn btn-sm btn-ghost text-xs", onClick: stopScanner, children: "Detener c\u00E1mara" }) }))] })), _jsxs("form", { onSubmit: handleManualSubmit, className: "flex gap-2", children: [_jsx("input", { type: "text", value: manualCode, onChange: (e) => setManualCode(e.target.value), placeholder: "C\u00F3digo QR manual (tenantId:groupId:passengerId)", className: "input flex-1", enterKeyHint: "send" }), _jsx("button", { type: "submit", className: "btn btn-md btn-primary flex-shrink-0", disabled: !manualCode.trim(), children: "Registrar" })] }), cameraPermission === 'denied' && !cameraError && (_jsx("p", { className: "text-xs text-[var(--color-text-muted)] text-center", children: "C\u00E1mara no disponible. Usa el campo de texto para registrar manualmente." }))] }));
}
//# sourceMappingURL=QrScanner.js.map